import React, { memo, useCallback, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { InputRow } from "./InputRow";
import type { UnitConverter } from "../../components/MeasurementUnits/UnitConverter";
import { useAppDispatch, useAppSelector, useAppStore } from "../../store/hooks/hooks";
import type { IDDItem } from "../../components/common/DropdownMenu";
import { DownloadButton } from "../../components/common/DownloadButton";
import { CalculateButton } from "../../components/common/CalculateButton";
import DropdownMenu from "../../components/common/DropdownMenu";
import { setCriteria, setInputUnit, setInputValue, validateInputs } from "../../store/slices/hydratesCalculatorSlice";
import type { HydrateCriteria, IHydrateCalculator } from "../../interfaces/interfaces";
import { useCalculatorMutation } from "../../store/slices/apiSlice";

interface CalculatorInputsSectionProps {
	converter: UnitConverter;
}

const EMPTY_INPUT_IDS: string[] = [];

const InputsSection: React.FC<CalculatorInputsSectionProps> = ({
	converter,
}) => {
	const calculator = useAppSelector(state => state.hydratesCalculator);
	const [triggerCalculator, { isLoading }] = useCalculatorMutation();
	const dispatch = useAppDispatch();

	const inputIds = useMemo(() => {
		if (!calculator) {
			return EMPTY_INPUT_IDS;
		}
		return calculator.inputs.map(input => input.id);
	}, [calculator.inputs]);

	const unitsDDItems: { [key: string]: IDDItem[] } = useMemo(() => {
		if (!calculator) {
			return {};
		}

		return calculator.inputs.reduce<Record<string, IDDItem[]>>((acc, input) => {
			const units = converter.getUnitsByCategory(input.unitCategoryId);
			acc[input.id] = units.map(unit => ({
				id: unit.id,
				translationKey: `hydrates:measurement_units.${unit.category}.${unit.id}`
			} as IDDItem));
			return acc;
		}, {});
	}, [calculator, calculator.inputs]);

	const onSelectCriteria = useCallback((selectedCriteria: string) => {
		if (selectedCriteria as HydrateCriteria)
			dispatch(setCriteria(selectedCriteria as HydrateCriteria));

	}, [dispatch]);

	const validated = (freshCalc: IHydrateCalculator) => {
		return !freshCalc.inputs.some(input => input.error != null);
	}

	const buildFormData = (freshCalc: IHydrateCalculator): Record<string, string | number> => {
		const formData: Record<string, string | number> = {};
		freshCalc.inputs.forEach(input => {
			formData[input.queryName] = input.baseValue ?? 0;
		});
		formData["criteria"] = freshCalc.criteria.selected;

		return formData;
	};

	const getFormData = useCallback(() => {
		if (!calculator) {
			console.error('Calculator state is missing!');
			return;
		}

		dispatch(validateInputs());

		if (!validated(calculator))
			return;

		return buildFormData(calculator);
	}, [calculator, dispatch]);

	const handleCalculate = useCallback(async () => {
		const formData = getFormData();
		if (formData) {
			triggerCalculator({
				calculator_type: "hydrate_formation",
				...formData,
			});
		}
	}, [triggerCalculator, calculator]);

	const handleInputChange = useCallback(
		(inputName: string, value: number | null) => {
			if (!calculator) {
				return;
			}

			dispatch(setInputValue({ inputName, value }));
		}, [dispatch]);

	const handleSelectUnit = useCallback((inputId: string, toUnitId: string) => {
		if (!calculator) {
			return;
		}

		dispatch(setInputUnit({ inputId, toUnitId }));
	}, [dispatch]);


	const { t } = useTranslation();
	return (<div className="hydrates__inputs-section">
		<fieldset className="hydrates__form round-corner-container">
			<legend>{t('main_parameters')}</legend>
			<div className="hydrates__inputs">
				{inputIds.map((inputId) => (
					<InputRow
						key={inputId}
						inputId={inputId}
						dropdownItems={unitsDDItems[inputId]}
						onInputChange={handleInputChange}
						onSelectUnit={handleSelectUnit}
					/>
				))}
			</div>
			<div className="hydrates__criteria">
				<DropdownMenu
					inputId={calculator.criteria.id}
					items={calculator.criteria.options.map(option => ({ id: option, translationKey: `hydrates:criteria_options.${option}` } as IDDItem))}
					selectedItemId={calculator.criteria.selected}
					onSelect={onSelectCriteria}
				/>
			</div>
		</fieldset>
		<div className="hydrates__buttons">
			<CalculateButton onClick={handleCalculate} disabled={isLoading} />
			<DownloadButton getFormData={getFormData} calculatorType="hydrate_formation" disabled={false} title={t(`download`)} />
		</div>
	</div>
	);
};

export default memo(InputsSection);
